/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from '@vlinder-web/plugin-agent-service-react';
import { featurePayoutDetailConfig } from '@pages/PayoutDetailPage/config/featuresSection.config';
import { GET_TRANSACTION_LIST } from '@pages/PayoutDetailPage/apis/graphql';

import { Graphql } from '@common/graphql';
import { apiConfig } from '@pages/PayoutDetailPage/config/api.config';
import { vappContainer } from '@/application';
import { AppNavigatorService } from '@/AppNavigator/services';
import { usePolicyId } from '../../provider';
import { toHeaderCase } from 'js-convert-case';
import moment from 'moment';

const loginSrv = () => {
  return vappContainer.get<AppNavigatorService>(
    AppNavigatorService.displayName
  );
};

export interface ITransactionPayoutDetailMethods extends IPluginMethodMap {
  getTransactionPayoutDetailProps(args: any): Promise<any>;
}

export class TransactionPayoutDetailPlugin implements IAgentPlugin {
  readonly methods: ITransactionPayoutDetailMethods;

  constructor() {
    this.methods = {
      getTransactionPayoutDetailProps: this.getProps.bind(this),
    };
  }

  async getProps(args: any) {
    //return await this.getTransactionPayoutDetail(args);
    return {
      feature: featurePayoutDetailConfig,
    };
  }

  private async getTransactionPayoutDetail(args: any) {
    const { policyNumber, userVid } = usePolicyId();
    try {
      let gql = Graphql.withBaseUrl(apiConfig.gql.url);

      // const token = getToken();
      const token = loginSrv()?.getToken();
      if (token) {
        gql = gql.withHeaders({
          authentication: token,
        });
      }
      const client = gql.getClient();
      const res = await client
        .query(GET_TRANSACTION_LIST, {
          input: {
            policyId: policyNumber,
          },
          input1: {
            limit: 100,
            where: [
              {
                column: 'txnOwner',
                op: 'Equal',
                value: [userVid],
              },
              {
                column: 'type',
                op: 'In',
                value: ['POLICY_PURCHASE_FLOW', 'KYC_SUBMISSION'],
              },
              {
                column: 'txnSetId',
                op: 'In',
                value: [policyNumber, null],
              },
            ],
          },
        })
        .toPromise();
      if (res?.data?.getTxnList) {
        let _txtResult = res?.data?.getTxnList?.data;
        return {
          accordionItems: this.getAccordionItems(_txtResult),
          timelineData: res?.data?.getTxnList,
        };
      }
    } catch (err: any) {
      console.log(`Sign In user error`, err?.response?.data);
      throw Error(err?.response?.data?.error?.display_message);
    }
  }

  private getAccordionItems(txList: any) {
    let items: any = [];
    if (txList?.length > 0) {
      let timelineDetails = this.getTimeLine(txList);
      items.push({
        type: 'timeline',
        title: featurePayoutDetailConfig?.timelineDetails?.title,
        data: [...timelineDetails],
        lineColor: '#262B40',
        horizontalStepperColors: {
          DONE: '#56DCBA',
          UNDONE: '#56DCBA',
          LAST: '#56DCBA',
          CURRENT: '#56DCBA',
        },
      });
    }
    return items;
  }

  private getTimeLine(data: any) {
    let _temp: any = [];
    Array.isArray(data) &&
      data?.forEach((element) => {
        _temp.push({
          key: element?.id,
          headline: toHeaderCase(element?.action ?? ''),
          headlineBackgroundColor: '#579783',
          children: this.getChildren(element),
        });
      });

    return _temp;
  }

  private getChildren(data: any) {
    let _temp: any = [];
    _temp.push({
      itemId: data?.id,
      multiColList: this.getProvenanceAccordion(data),
      title: toHeaderCase(data?.action ?? ''),
      subtitle: data?.when ? moment(parseInt(data?.when)).format('LLL') : null,
    });

    return _temp;
  }

  private getProvenanceAccordion(res: any) {
    let _temp: any = [];
    if (res) {
      let _list: any = [];
      let _date: any = [];
      let _location: any = [];
      if (res?.performedBy?.firstName) {
        _list.push({
          title: toHeaderCase('by'),
          subTitle: `${toHeaderCase(
            res?.performedBy?.firstName
          )} ${toHeaderCase(res?.performedBy?.lastName)}`,
          icon: featurePayoutDetailConfig?.orgIcon,
        });
      }
      if (res?.location) {
        _location.push({
          title: toHeaderCase('at'),
          subTitle: res?.location ?? '-',
          icon: featurePayoutDetailConfig?.locationIcon,
        });
      }
      if (res?.when) {
        _date.push({
          title: toHeaderCase('on'),
          subTitle: res?.when ? moment(parseInt(res?.when)).format('LLL') : '-',
          icon: featurePayoutDetailConfig?.dateIcon,
        });
      }
      _temp = [..._list, ..._location, ..._date];
    }
    return {
      numberOfColumns: '2',
      data: _temp,
    };
  }
}
